import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import KebabMenu from '@components/common/KebabMenu'
import reportService from '@api/v1/reportService'
import reportJobService from '@api/v1/reportJobService'
import { toast } from '@store/toastStore'
import { deriveState, jobState } from '@config/queueConfig'
import useFirmName from '@hooks/useFirmName'
import { fmtDisplayDateTime } from '@utils/displayDate'
import { viewFromJobResult, viewFromTabularResult } from './jobResultView'
import { analyticsReportName, isExagoReportId, jobDisplayName } from './jobIdentity'
import { statementJobView, statementKindOf, groupedResultView, CRITERIA_NOT_RECORDED } from './statementJobView'
import JobRunSummary from './JobRunSummary'
import JobFailedPanel from './JobFailedPanel'
import JobExpiredPanel from './JobExpiredPanel'
import { isResultGone } from './resultRetention'
import { fmtRowCount, fmtTime, fmtWhen } from './queueFormat'
import { savedFilterChips } from './savedFilterChips'
import { exagoFiltersUsed, readExagoRunForDrawer } from './exagoFiltersUsed'
import { SAVED_IN_REPORT } from '@/pages/reports/exagoPromptFields'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import { downloadNoteFor, NOT_A_TABLE } from './jobDownloadNote'
import { canOpenRunResult } from './jobVisibility'
import { trackExport } from '@/analytics/pendo'
import { ArrowRightIcon, ClockIcon, DownloadIcon, DuplicateIcon, TrashIcon, XIcon } from './queueIcons'
import { STATE_ICONS } from './stateIcons'
/* KEYS ONLY. Importing `registry.js` here pulls all 20+ specialized page
   components — and their services — into the queue's bundle; it surfaced as three
   unrelated suites failing to load. See specializedActionKeys.js. */
import { isSpecializedActionKey } from '@/pages/reports/specializedActionKeys'

/**
 * Whether the drawer reads this run's stored result: a ready run that is not generic and has
 * no processId (CC-50233; see the effect below). Spec 215 asks the same question to know when
 * that read is still in flight, so the two can never disagree.
 */
const readsStoredResult = ({ id, state, effectiveState, kind, processId } = {}) =>
  !!id && (effectiveState || state) === 'ready' && kind !== 'generic' && !processId

/*
 * The "Filters used" value formatter (`fmtSavedParam`) and its date guard moved to
 * `savedFilterChips.js` (spec 167), which also names each chip's column from the
 * report's definition. Every rule that stood here is kept there, verbatim.
 */

/**
 * The drawer footer's filled primary — spec 119 / C1, "the Open report CTA looks
 * a little bulky" (Himaad, 2026-09-08).
 *
 * ## Why this is a utility string and not `.btn-cb-primary`
 *
 * Both of the obvious levers are closed:
 *
 *   1. Editing `.btn-cb-primary` in `src/index.css` repaints every CTA in the app
 *      AND breaks two e2e gates that assert the class measures exactly 38px
 *      (`e2e/reports-grid-audit.spec.js:512`, `e2e/financial-reports-audit.spec.js:219`).
 *      Those gates mount a THROWAWAY `.btn-cb-primary` probe rather than reading a
 *      real button, so they stay green here precisely because the class is
 *      untouched — this file simply stops wearing it.
 *   2. Adding `px-`/`py-`/`rounded-`/`text-`/`h-` utilities BESIDE the class does
 *      nothing: `src/index.css` is entirely unlayered while Tailwind v4 emits
 *      utilities inside `@layer utilities`, so the class wins regardless of
 *      specificity. This call site proved it — it carried
 *      `flex items-center justify-center gap-2`, and ALL FOUR were inert, because
 *      the shared `.btn-cb-*` base rule already sets `display:inline-flex`,
 *      `align-items:center`, `justify-content:center` and `gap:0.5rem`
 *      (`src/index.css:280-294`). They are live again now and are load-bearing:
 *      the ready-state CTA lays out a label plus an arrow icon.
 *
 * So the class comes off and the button is rebuilt from utilities that actually
 * apply. `src/index.css` is spec 116's file this round and is not touched.
 *
 * ## What changed, and what deliberately did not
 *
 * Measured against the class it replaces:
 *
 *   | property   | .btn-cb-primary        | here            |
 *   |------------|------------------------|-----------------|
 *   | padding    | 8px 20px               | 8px 17px        |
 *   | border     | 1px solid #1d4ed8      | none            |
 *   | height     | 38px                   | 36px            |
 *   | radius     | 6px (--radius-rc-control) | 8px          |
 *   | box-shadow | var(--shadow-cb)       | none            |
 *   | hover      | shadow-cb-lg + translateY(-1px) | bg only |
 *
 * The 13px-blur drop shadow and the 1px hover lift are most of what read as
 * "bulky" — a 2px height change on its own would not have been the fix — so
 * neither is carried across, per the build brief.
 *
 * The 8px/17px padding is the BUILD BRIEF's value, not a measured mock inset. Said
 * plainly because an earlier draft of this comment and of spec 119 both attributed
 * it to "Himaad's mock inset", and nothing was measured: the Figma MCP seat quota
 * is exhausted and Dev Mode is unapproved, so only line-box heights were readable
 * from the frames this round (spec 119 §2.1) and a horizontal inset is not one.
 * If his frame is in fact still at 20px, this narrows the inset by 3px per side —
 * which nothing in this change can observe, since jsdom compiles no Tailwind. It
 * needs a look, not a citation.
 *
 * The border existed ONLY to force the 38px box that the two welded Run/Refresh
 * halves need, which this button is not.
 *
 * Fill stays the token, not a literal: `bg-rc-chip-text` IS `--color-rc-chip-text`
 * #1d4ed8, the same var `.btn-cb-primary` reads, so a repaint still happens in one
 * place. The name reads oddly as a background — it is the token's name, and
 * `DataGrid.jsx:1165` already uses it as a fill for the same reason.
 *
 * Contrast, measured 2026-09-08 (sRGB relative luminance, (L1+0.05)/(L2+0.05)):
 *   - #ffffff on #1d4ed8 = **6.702:1** — clears SC 1.4.3's 4.5 floor for 14px text.
 *   - #ffffff on hover `blue-800` #1e40af = **8.722:1** — darker, so strictly safer.
 *
 * 36px clears SC 2.5.8's 24x24 target floor. `cursor-pointer` is explicit because
 * `.btn-cb-primary` supplied `cursor: pointer` and a bare `<button>` defaults to
 * `cursor: default` — dropping the class would otherwise have silently regressed
 * the pointer affordance on the two `<button>` variants.
 *
 * ## The focus ring is INHERITED, and is not owned by this constant
 *
 * `src/index.css:200-203` carries a GLOBAL, **unlayered**
 * `:focus-visible { outline: 2px solid var(--color-cb-navy); outline-offset: 2px }`.
 * That file has no `@layer` wrapper anywhere while Tailwind v4 emits utilities
 * inside `@layer utilities`, and an unlayered normal declaration beats a layered
 * one regardless of specificity — the same mechanism as point 2 above. So a
 * `focus-visible:outline-*` utility written HERE would be inert. Four of them were
 * (`outline`, `outline-2`, `outline-offset-2`, `outline-cb-navy`); they are deleted
 * rather than left to read as a local guarantee, per this repo's rule that an inert
 * utility beside a winning rule is dead code that lies to the next reader.
 *
 * That inheritance is also exactly WHY dropping `.btn-cb-primary` is safe for
 * SC 2.4.7: the class had no `:focus-visible` rule of its own either, so the ring
 * this button shows is byte-identical to the one it showed before C1.
 *
 * The offset is SEPARATION, not contrast — an earlier draft of this comment said
 * otherwise and was wrong. A CSS outline paints OUTSIDE the border box at every
 * non-negative offset, so even at `outline-offset: 0` the ring sits entirely on the
 * footer's `bg-gray-50` #f9fafb, where `cb-navy` **#09203d** (`src/index.css:27` is
 * the authority; the #00285c in `cb2/CLAUDE.md`'s Brand section is stale) measures
 * **15.631:1** against SC 1.4.11's 3.0. Navy's **2.437:1** directly on this button's
 * #1d4ed8 fill can only bite with a NEGATIVE offset or an inset ring (`ring-inset`,
 * an inset `box-shadow`) — none of which is in play. So a zero-offset ring here
 * would read tighter, not invisible.
 *
 * `ReportJobDrawer.chrome.test.jsx` still pins that global rule's existence, its
 * width and its non-zero offset: existence and width because they ARE the SC 2.4.7
 * guarantee this file deliberately no longer states for itself, and the offset only
 * as the visual separation this control was designed with. `src/index.css` belongs
 * to spec 116 this round, so scoping, layering or removing that rule would
 * otherwise unring this CTA with nothing going red.
 *
 * ## `forced-colors:outline-1` replaces a boundary this change removed
 *
 * In Windows High Contrast the UA forces `background-color` to ButtonFace, `color`
 * to ButtonText and drops `box-shadow` — so a filled button with no border becomes
 * indistinguishable from static text. `.btn-cb-primary` carried
 * `border: 1px solid var(--color-rc-chip-text)` (`src/index.css:304`, present only
 * to force its 38px box), which would have been repainted as ButtonBorder and kept
 * the control visible; Tailwind v4 preflight sets `border-width: 0` on `*`, so
 * dropping the class left nothing behind it. An outline costs no layout, so the
 * 36px box C1 exists to produce is preserved. Unlike the deleted focus utilities
 * this one DOES apply: nothing unlayered sets `outline-width` in the non-focus
 * state. (App-wide this gap is pre-existing — `forced-colors` appears nowhere else
 * in `src/` — so this fixes only the boundary this diff itself removed.)
 *
 * ONE utility, not two. An earlier draft paired `forced-colors:outline` with
 * `forced-colors:outline-1`; compiled through the installed Tailwind 4.2.1 both emit
 * the IDENTICAL declaration — `outline-style: var(--tw-outline-style);
 * outline-width: 1px`, where that custom property is `@property`-registered with
 * `initial-value: solid`. The duplicate is dropped: two tokens for one declaration
 * pins a class COUNT rather than a rendered boundary, and a later reader deleting
 * the redundancy would have turned three assertions red with no rendering change.
 *
 * ## Why all THREE footer primaries take it, not just "Open report"
 *
 * Himaad named the Open report CTA, but the footer has ONE primary slot whose
 * label depends on state (`Open report` / `Change filters & retry` /
 * `Run again, same filters` when expired). Slimming only the ready state would put
 * a 36px flat button and a 38px shadowed one in the same pixel position depending
 * on state — reintroducing exactly the "the primary moves around depending on
 * state" problem the footer redesign existed to fix. The `.btn-cb-toolbar`
 * secondaries are a different tier and are untouched.
 */
const PRIMARY_CTA =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-rc-chip-text ' +
  'px-[17px] py-2 text-[14px] font-semibold leading-5 text-white transition-colors ' +
  'hover:bg-blue-800 forced-colors:outline-1'

/**
 * The run's name, as the drawer's heading, the PDF masthead and running foot, the
 * XLSX title row and the download filename all print it.
 *
 * `displayName` is the catalog report name — `ReportQueue`'s `openJob` names every
 * job it hands this drawer, listed or not (spec 132). The fallback is the SAME chain
 * (`jobIdentity.jobDisplayName`), reached only when the catalog lacks the id or its
 * read has not resolved — and then the title still carries the report id.
 */
const jobTitle = (job) => job.displayName || jobDisplayName(job)

/**
 * The download's file name: a kebab slug of the title, so the worst case is
 * `report-34.pdf`, never `report.pdf` (spec 132 decision 4).
 *
 * Deliberately NOT `reportGridUtils.exportFileName`, the helper the report pages use:
 * it stamps the CLICK time into the name, and this is a SAVED result — a Balance Sheet
 * downloaded today from Monday's run must not be filed under today.
 */
const downloadSlug = (job) => String(jobTitle(job)).replace(/[^\w.-]+/g, '-').toLowerCase()

/**
 * The download masthead (spec 127 §4.6, I30) — the keys `buildPdf` actually reads.
 *
 * The drawer used to pass `{ reportName, generatedAt }`, two keys neither writer reads,
 * so every PDF downloaded from here had NO masthead: no firm, no report name, no run
 * time (verifier 5) — and the Excel file no title row. Now it is the page's own
 * masthead (`ReportExport.jsx` handlePdf / handleExcel), with two deliberate
 * differences, both because this is a SAVED result:
 *
 *  - "Generated" is when the DATA was produced — the run's finish time (`finishedAt`,
 *    derived as created + duration; `createdAt` when there is none) — never the click.
 *    A statement printed today from Monday's run must not claim today's figures.
 *  - The criteria line exists only for a statement job (`statementJobView`), rebuilt
 *    from the run's own `savedParams`; when its dates or basis cannot be read it says
 *    so in the shell's words rather than going silent, because an export header with
 *    no filters line reads as "no filters were applied". Every other report prints no
 *    criteria line, as before.
 *
 * PDF and Excel word the criteria line as the page does ("Filters applied:" /
 * "Filters —").
 */
const downloadMeta = (format, { job, firmName, userName, statement }) => {
  const when = fmtDisplayDateTime(job.finishedAt ?? job.createdAt)
  const generatedLine = [when ? `Generated ${when}` : '', userName ? `Printed by ${userName}` : '']
    .filter(Boolean).join(' · ') || undefined
  const criteria = statement ? (statement.filtersSummary || CRITERIA_NOT_RECORDED) : null
  const filtersLine = criteria
    ? (format === 'pdf' ? `Filters applied: ${criteria}` : `Filters — ${criteria}`)
    : undefined
  return { firmLine: firmName, title: jobTitle(job), filtersLine, generatedLine }
}

/**
 * Screen 2 — the job detail drawer.
 *
 * Structure follows Himaad's Section 3 (`Job / Running` 589:9789, `Job / Ready`
 * 589:9837, `Job / Didn't finish` 589:9885):
 *
 *   fixed top       status chip → title → `Report #id · you` → snapshot notice
 *   scrolling middle ONE BIG METRIC + subline + definition list (JobRunSummary),
 *                    then the per-state diagnostic panel if there is one, then
 *                    filters used, then previous runs
 *   fixed footer     ONE filled primary + one secondary + kebab, per state
 *
 * The one-primary rule still holds; what changed is WHERE. "Open report" used to
 * be a full-width filled button in the fixed top, and the footer carried two
 * equal-weight toolbar buttons — so the drawer's most important action moved
 * around depending on state, and the footer never carried the answer. Now every
 * state's answer is the footer's primary and the top is identity only.
 */
const ReportJobDrawer = ({ job, allJobs = [], onClose, onRerun, onDuplicate, onDismiss, onCancelRun, now = 0 }) => {
  const panelRef = useRef(null)
  const closeRef = useRef(null)
  const previousFocusRef = useRef(null)
  const [downloading, setDownloading] = useState(null)
  // The stored result for a NON-generic run: { status: 'idle'|'loading'|'ready'|'unsupported'|'error' },
  // plus, for a statement job, `statement` = statementJobView's { kind, view, filtersSummary }.
  const [stored, setStored] = useState({ status: 'idle' })
  /*
   * Spec 152 — the id of a job whose stored result the server answered 410 for (the
   * stored-result read below, or a generic Download). The bucket deletes by age, and
   * a result can go before the expiry we were told; when it has, the drawer says so as
   * a STATE — the Expired chip, the expired panel, no Open or Download — instead of a
   * Ready drawer and a toast. Keyed on the id so opening another run starts clean.
   */
  const [goneJobId, setGoneJobId] = useState(null)
  /*
   * Spec 215 — an Advanced (Exago) run's stored result, as its "Filters used" reads it:
   * `{ jobId, run }` once the read below has SETTLED for that job (`run` null when it failed
   * or did not read). Keyed on the id, so a drawer moving to another run never states the
   * previous run's filters, and "still reading" is told apart from "read, and nothing there".
   */
  const [exagoRead, setExagoRead] = useState({ jobId: null, run: null })
  // Spec 127 §4.6: the masthead the page's own PDF carries (ReportExport.jsx). The drawer
  // is mounted once by the queue page, so this is that page's one firm-name read.
  const firmName = useFirmName()
  const { user } = useAuth0()

  /**
   * A specialized or accounting run has no CB `processId` — only the generic engine
   * mints one — so its result can only come from its own stored blob (CC-50233).
   * Fetch it when the drawer opens, for two reasons at once: it decides whether a
   * download is even possible from the REAL payload rather than a hardcoded list of
   * action keys that would drift, and it means the click itself is instant.
   *
   * Generic runs are deliberately excluded: their stored blob is CB's raw
   * PascalCase object, which must be read through the enriched
   * `/reports/:id/data?processId=` path instead (see `download`).
   *
   * Spec 127 §4.6 (S7): a STATEMENT job — P&L / Comparative P&L / Balance Sheet, told
   * apart by the payload's shape — is built through its page's own models into the
   * page's default export view (`statementJobView`), so it prints CB's sections,
   * headings and totals rather than the raw export rows, and a P&L job (which the
   * tabular adapter cannot read) gains its downloads. Like the page's own reopen
   * (`PLReport.jsx` / `BalanceSheetReport.jsx`: `Promise.all([status, result])`), it
   * reads the run's `savedParams` from the job's status: they decide a P&L's compare
   * columns and matrix mode, and they are the criteria line. A status read that fails
   * is not a failed download — the statement job view decides what it can still print
   * without them (a Balance Sheet: everything but the criteria; a P&L: nothing).
   */
  useEffect(() => {
    const read = readsStoredResult({
      id: job?.id, state: job?.state, effectiveState: job?.effectiveState, kind: job?.kind, processId: job?.processId,
    })
    if (!read) {
      setStored({ status: 'idle' })
      return undefined
    }
    let stale = false
    const jobId = job.id
    const exago = isExagoReportId(job.reportId)
    setStored({ status: 'loading' })
    reportJobService
      .result(jobId)
      .then(async (payload) => {
        if (stale) return
        // Spec 215: an Advanced (Exago) run's stored body is what its "Filters used" states,
        // read through the page's own reader. A body that does not read is kept as nothing,
        // and the drawer falls back to the dates the run was sent.
        if (exago) setExagoRead({ jobId, run: readExagoRunForDrawer(jobId, payload) })
        if (statementKindOf(payload)) {
          const savedParams = await reportJobService.status(jobId)
            .then((s) => s?.savedParams ?? null)
            .catch(() => null)
          if (stale) return
          const statement = statementJobView(payload, { savedParams })
          // Spec 167: the refusal's own reason travels, so the note can say WHY.
          setStored(statement?.view
            ? { status: 'ready', view: statement.view, statement }
            : { status: 'unsupported', reason: statement?.refused || NOT_A_TABLE })
          return
        }
        const view = viewFromTabularResult(payload)
        setStored(view ? { status: 'ready', view } : { status: 'unsupported', reason: NOT_A_TABLE })
      })
      .catch((err) => {
        if (stale) return
        // A failed read settles with nothing; a throw AFTER the body was read keeps that read.
        if (exago) setExagoRead((prev) => (prev.jobId === jobId ? prev : { jobId, run: null }))
        // Spec 152 — a 410 is not a read error: the result is gone, and the drawer
        // becomes the Expired state rather than offering "Download unavailable".
        if (isResultGone(err)) {
          setGoneJobId(jobId)
          setStored({ status: 'idle' })
          return
        }
        setStored({ status: 'error', message: err?.message })
      })
    return () => { stale = true }
  }, [job?.id, job?.kind, job?.processId, job?.state, job?.effectiveState, job?.reportId])

  /*
   * Spec 167 — the report's definition, read only to NAME the "Filters used" chips.
   *
   * A generic run's `savedParams` is keyed by CB columnId, so without this the chip
   * read "1199: |" (run 4058d0f0 on QA) instead of "Date Picker: All Dates". Only a
   * generic run with stored params asks: a specialized or accounting run's params are
   * keyed by field name, and its definition carries no columns (report 21's has none).
   *
   * Keyed by report id, and the answer is used only while it is for THIS run's report.
   * A failed or empty read leaves `columns` null and every chip prints exactly what it
   * printed before — the read is a label lookup, so it must never be able to hide or
   * change a value. Read through `Promise.resolve().then` so a synchronous throw
   * becomes that same quiet fallback rather than a crashed drawer.
   */
  const definitionReportId = job?.kind === 'generic' && job?.reportId != null
    && job?.savedParams && Object.keys(job.savedParams).length > 0
    ? String(job.reportId)
    : null
  const [definition, setDefinition] = useState({ reportId: null, columns: null })
  useEffect(() => {
    if (!definitionReportId) return undefined
    let stale = false
    Promise.resolve()
      .then(() => reportService.getDefinition(definitionReportId))
      .then((d) => {
        if (!stale) setDefinition({ reportId: definitionReportId, columns: Array.isArray(d?.columns) ? d.columns : null })
      })
      .catch(() => {
        if (!stale) setDefinition({ reportId: definitionReportId, columns: null })
      })
    return () => { stale = true }
  }, [definitionReportId])
  const definitionColumns = definition.reportId === definitionReportId ? definition.columns : null

  /**
   * Download a finished run straight from the drawer (spec 059, extended by
   * CC-50233 to the runs that have no processId).
   *
   * Generic: the stored R2 blob is CB's RAW PascalCase payload, which the viewer
   * can't read — the same trap that made reused jobs render "No data" (commit
   * e3b093f). So it goes through the enriched `/reports/:id/data?processId=` path,
   * exactly like reopening the report does, and adapts THAT.
   *
   * Everything else: the stored blob IS the shape that report's own page consumes,
   * so it is the right source, and it is already in hand from the effect above.
   * A statement job's view is its page's default export view (spec 127 S7).
   *
   * Both formats carry the page's masthead (`downloadMeta`, spec 127 §4.6 / I30).
   *
   * ExcelJS and jsPDF are both lazy-loaded by the writers, so opening a drawer
   * never pays for them.
   */
  const download = useCallback(async (format) => {
    if (!job?.id) return
    setDownloading(format)
    try {
      let view = null
      if (job.processId) {
        if (!job.reportId) return
        // Spec 152 — NAMED, so cb-api reads this run's own stored copy and, when the
        // bucket has deleted it, answers 410 instead of CB's 500 for a long-gone file.
        const result = await reportService.getData(job.reportId, job.processId, { jobId: job.id })
        // A grouped result prints what its page prints — headings, rows, CB's
        // subtotal lines (spec 127 §4.6); a flat one keeps the flat adapter.
        view = groupedResultView(result) || viewFromJobResult(result)
      } else if (stored.status === 'ready') {
        view = stored.view
      }
      if (!view) { toast.error('That result has no columns to export.'); return }
      const safe = downloadSlug(job)
      const meta = downloadMeta(format, {
        job,
        firmName,
        userName: user?.name,
        statement: !job.processId && stored.status === 'ready' ? stored.statement : null,
      })
      /*
       * spec 094 — emitted AFTER the view resolves, so an attempt that found
       * no exportable result is not counted as an export. Always engine
       * 'client': this drawer builds both formats in the browser from the
       * stored result, and offers no CSV.
       */
      trackExport({
        reportId: job.reportId,
        // The CATALOG name or nothing (spec 132): a fallback title ("Report 34") is an
        // identifier, and must not enter the export series as though it were a name. An
        // Advanced Report's name is firm-authored, so it never enters it (spec 209 §3).
        reportName: analyticsReportName(job),
        format,
        engine: 'client',
        exportPath: 'client_view',
        surface: 'job_drawer',
        rowCount: Array.isArray(view?.rows) ? view.rows.length : undefined,
      })
      if (format === 'xlsx') {
        const { exportClientExcel } = await import('../exportClientView')
        await exportClientExcel(view, {}, `${safe}.xlsx`, meta)
      } else {
        const { exportClientPdf } = await import('../exportClientPdf')
        await exportClientPdf(view, {}, `${safe}.pdf`, meta)
      }
    } catch (err) {
      // Spec 152 — the result is gone: say so as the drawer's state, not as a toast
      // that vanishes and leaves a Ready drawer offering the same dead Download.
      if (isResultGone(err)) { setGoneJobId(job.id); return }
      toast.error(err?.message || 'Could not build that download.')
    } finally {
      setDownloading(null)
    }
  }, [job, stored, firmName, user?.name])

  // Focus management: trap focus in the drawer, Escape closes, focus returns to
  // whatever opened it.
  //
  // Keyed on the job's ID, not the job object (spec 132, WCAG 2.4.3). The object is
  // rebuilt whenever its inputs are: the list poll (every 5 s) re-merges an open
  // list row, and naming a detail-only job makes it depend on the name maps. Each
  // new object re-ran this effect, which moved focus back to "Close job details" —
  // so a keyboard user on the kebab lost their place every poll tick. A NEW job (a
  // different id) is still a new dialog and still takes focus.
  const openJobId = job?.id ?? null
  useEffect(() => {
    if (!openJobId) return undefined
    previousFocusRef.current = document.activeElement
    closeRef.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose?.(); return }
      if (e.key !== 'Tab') return
      const focusable = panelRef.current?.querySelectorAll('a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])')
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previousFocusRef.current?.focus?.()
    }
  }, [openJobId, onClose])

  /*
   * Previous runs of the SAME report — the history table at the foot of the drawer,
   * and the source of the "vs last run" comparison in the blocks.
   *
   * PREVIOUS means created BEFORE this run (spec 132). The filter was only "same
   * report", so a run's "last run" could be a NEWER one — and on a detail-only drawer
   * it always was: `allJobs` is the newest 50, and a run outside them is older than
   * every one. Measured live: a 09/29 scheduled 190 run read "VS LAST RUN 113 more"
   * against a 09/30 run. A wrong delta is a wrong figure; with no older run in hand
   * the comparison is simply not shown. A run with no `createdAt` compares to nothing.
   * (Same schedule / same filters is a further question, left to Jordan.)
   */
  const history = useMemo(() => {
    if (!job || job.createdAt == null) return []
    return allJobs
      .filter((j) => j.id !== job.id && j.reportId === job.reportId && j.createdAt != null && j.createdAt < job.createdAt)
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 3)
  }, [job, allJobs])

  if (!job) return null

  // One resolved state for the whole panel — see JobRowActions for why
  // re-deriving separately is how an expired job gets a live "Open report".
  //
  // Spec 152 — and a ready run whose result the server answered 410 for IS expired,
  // whatever its expiry said: the one fact that outranks a date is the object's absence.
  const derived = job.effectiveState || deriveState(job, now)
  const resultGone = goneJobId === job.id && derived === 'ready'
  const state = resultGone ? 'expired' : derived
  // Everything below the header reads `shown.effectiveState`, so it must ALWAYS carry the
  // resolved state — not only when a 410 changed it. A run that is not in the list rows (a
  // deep link, Schedules "Last run", Run history, an email link) arrives from the detail
  // read with no `effectiveState` at all; `JobRunSummary` then fell back to the raw
  // `ready` and printed "kept while it is one of this schedule’s last 12 runs" directly
  // above the panel saying the data is gone (spec 152 review, QA run 410cf866).
  const shown = job.effectiveState === state ? job : { ...job, effectiveState: state }
  const vocab = jobState(state)
  const Icon = STATE_ICONS[vocab.icon] || STATE_ICONS.dots
  // CC-50231 TC4b: ownership gates opening and downloading here too — the drawer is
  // reachable from a firm/group listing, and offered "Open report" plus a download on a
  // colleague's run. The server refuses both (`getJob` is owner-scoped, verified live as
  // a 404), so this removes affordances that could not work rather than closing a leak.
  // `!== false` so an absent flag still means "mine".
  const isOwner = job.isOwner !== false
  // Spec 169 — a CB administrator may also open another user's PRIVATE-schedule run,
  // when cb-api said so for this reader (`adminAccess`). Dismiss and Cancel stay
  // owner-only below: opening a result is not owning the run.
  const adminAccess = !isOwner && job.adminAccess === true
  const canView = canOpenRunResult(job) && state === 'ready' && job.reportId
  /* The scheduled/recurring test moved INTO `JobRunSummary`, which is now the one
     component that decides what a state's headline figure and labels are — the
     drawer no longer needs to know, and two places deciding it is how the old
     "Scheduled run" heading and the blocks' own scheduled branch drifted apart. */
  const titleId = `job-drawer-title-${job.id}`

  // Download is offered for a result that is genuinely still fetchable: a ready
  // generic run WITH its processId, or a ready non-generic run whose stored blob
  // adapted into a table. Anything else would hand the user a button that produces
  // an empty file.
  const canDownload = canView && (!!job.processId || stored.status === 'ready')
  // ...and when it is NOT possible, say so instead of omitting the affordance. A
  // missing button is indistinguishable from a regression, which is precisely how
  // this arrived back from QA (CC-50233): the download was never built for the
  // no-processId shapes, and the silence read as "it used to work".
  //
  // Spec 167: SAID, in the drawer's body, with the reason. This used to be a disabled
  // kebab item — unseen until the menu opened, painted at 1.472:1, and not a
  // menuitem — so a fresh Aged A/R run read as "no Download, no reason" on QA even
  // though the sentence existed. `jobDownloadNote.js` has the measured mechanism.
  const downloadNote = downloadNoteFor({ isOwner, adminAccess, canView, canDownload, stored })
  // Spec 167: the chips name their column from the definition when it has arrived.
  const filterChips = savedFilterChips(job.savedParams, definitionColumns)
  // Spec 215: an Advanced (Exago) run states what the run says it ran with, in the page's
  // words — never its stored keys. Only a stored result read for THIS job counts, and while
  // that read is in flight nothing is said (no wording that then changes). "In flight" is the
  // effect's OWN gate (`readsStoredResult`), not the drawer's derived state: a detail-only run
  // past its expiry derives 'expired' here yet is still read, and can still answer.
  const exagoJob = isExagoReportId(job.reportId)
  const exagoReadSettled = exagoRead.jobId === job.id
  const exagoFilters = exagoJob
    ? exagoFiltersUsed({
      run: exagoReadSettled ? exagoRead.run : null,
      savedParams: job.savedParams,
      pending: readsStoredResult(job) && !exagoReadSettled,
    })
    : null
  /*
   * ## The re-run gate, and why it is not `job.kind === 'generic'`
   *
   * "Run again, same filters" navigates to `/reports/:id?jobId=<id>&rerun=1`.
   * The effect that reads `?rerun=1` and starts a run lives in ReportView, and it
   * returns early — BEFORE reaching the rerun branch — for any report with a
   * registered specialized renderer (`ReportView.jsx:702`). No specialized page
   * reads `rerun` either. So on those reports the click reopens the saved
   * snapshot and **issues no run at all**, silently.
   *
   * That is not hypothetical for this drawer: two of the three states Himaad drew
   * are specialized reports (#25 Shareholder Compensation, #22 Fee Receipts). The
   * redesigned footer would have put a prominent, filled "Change filters & retry"
   * on a compensation report where it does nothing.
   *
   * The predicate asks the SAME question the early return asks — does this report
   * have a registered renderer — from the same key list, rather than a proxy for
   * it. `job.kind` is checked too, so a specialized run whose `actionKey` did not
   * survive into the job row still fails closed. Gating on `kind` alone would be
   * the proxy: `kind` is assigned when the job is created and is not what decides
   * whether the rerun effect runs.
   *
   * The keys come from `specializedActionKeys.js`, not from `registry.js`, purely
   * for bundle reasons — the registry statically imports every specialized page.
   * A test pins the two in sync so this stays one source of truth.
   */
  const rendersSpecialized = isSpecializedActionKey(job.actionKey)
  const canRerun = job.kind === 'generic' && !rendersSpecialized
  const rerunNote = 'Re-running from here isn’t wired up for this report yet — open it and press Run report'

  const footerItems = [
    ...(canDownload ? [
      {
        key: 'xlsx',
        label: downloading === 'xlsx' ? 'Building Excel…' : 'Download Excel',
        icon: <DownloadIcon />,
        onSelect: () => download('xlsx'),
      },
      {
        key: 'pdf',
        label: downloading === 'pdf' ? 'Building PDF…' : 'Download PDF',
        icon: <DownloadIcon />,
        onSelect: () => download('pdf'),
      },
    ] : []),
    /* Spec 167: the "why no download" note left this menu for the drawer body. */
    /* Moved out of the footer to make room for the state's primary. It is a
       variant-of action, not the state's answer, so the kebab is the right tier. */
    ...(canRerun ? [{ key: 'dupe', label: 'Duplicate & edit', icon: <DuplicateIcon />, onSelect: () => onDuplicate?.(job, 'edit') }] : []),
    /* Says what notification actually happens today rather than offering the
       design's "Notify me when it is ready", which has no durable channel behind
       it. Disabled = informational, the same convention as `downloadNote`. */
    ...((state === 'queued' || state === 'running') ? [{
      key: 'notify-note',
      label: 'You’ll get a browser notification while this tab stays open',
      icon: <ClockIcon />,
      disabled: true,
    }] : []),
    { key: 'copy', label: 'Copy job ID', icon: <DuplicateIcon />, onSelect: () => navigator.clipboard?.writeText(job.id).catch(() => {}) },
    // Owner-only: DELETE is owner-scoped server-side, so on a colleague's run this
    // removed the row on screen and the next poll restored it (CC-50231 TC4b).
    ...(isOwner ? [{ key: 'dismiss', label: 'Dismiss from queue', icon: <TrashIcon />, danger: true, dividerBefore: true, onSelect: () => { onDismiss?.(job); onClose?.() } }] : []),
  ]

  return (
    <>
      <div className="fixed inset-0 z-40 bg-cb-navy/40" onClick={onClose} aria-hidden="true" />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[452px] flex-col border-l border-gray-200 bg-white shadow-2xl"
      >
        {/* FIXED TOP */}
        <div className="border-b border-gray-200 px-5 pb-4 pt-4">
          <div className="flex items-start justify-between gap-2.5">
            <div className="min-w-0">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[13px] font-semibold ${vocab.badge}`}>
                <Icon />
                {/* S3-D14: the chip says `Ready`, matching the design and — more
                    to the point — matching the tab, the row status cell and the
                    bell, which all read the label from `jobState`. The special
                    case here was the only place in the app that said "Ready to
                    view", so the same run had two names depending on which
                    surface you were looking at. */}
                {vocab.label}
              </span>
              {/*
                The catalog report name, for EVERY job this drawer can open (spec 132).
                `ReportQueue`'s `openJob` names the job whether it came from the list
                or from a detail read alone — a run older than QUEUE_PAGE_SIZE, a deep
                link, a Schedules-page "Last run". The note that stood here said the
                drawer's own "Report {id}" fallback covered the detail-only case; it
                did, by printing "Report 190" on a named report, and that was the bug.
                The fallback (CC-50147: never an EMPTY heading on a financial run) is
                now reached only when the catalog lacks the id or has not loaded.
              */}
              <h2 id={titleId} className="mt-2 text-[19px] font-display leading-tight text-cb-navy">
                {jobTitle(job)}
              </h2>
              {/* S3-D14: `Report #162 · you`, per the design — whose run this is,
                  which is a fact the reader needs in a firm/group listing. It read
                  `· generic`, exposing our internal `kind` discriminator ("generic"
                  / "specialized" / "accounting") as though it were user-facing
                  vocabulary. Nobody outside this codebase knows what a generic
                  report is, and the field is still used for the re-run gate below,
                  where it belongs. */}
              <p className="mt-1.5 text-[13px] text-gray-500">
                {job.reportId ? `Report #${job.reportId}` : `Job ${String(job.id).slice(0, 8)}`}
                {` · ${isOwner ? 'you' : 'a teammate'}`}
              </p>
              {/* Spec 169 — why an administrator can open a run that is not theirs, said
                  where they will read it, before they open someone else's figures. */}
              {adminAccess && (
                <p data-testid="job-admin-access" className="mt-1.5 text-[13px] leading-snug text-gray-600">
                  You can open this run because you’re a Centerbase administrator and it came from a private schedule.
                </p>
              )}
            </div>
            {/*
              * `text-gray-500`, not `gray-400` — SC 1.4.11, raised in R3 review and
              * real. This control's ONLY visual affordance is the X glyph: no label,
              * no border, so the glyph IS the component's identifying visual
              * information and owes 3.0:1. #9ca3af on this white panel measures
              * **2.539:1** and failed; #6b7280 is **4.834:1**, and is already the
              * token this file uses for its secondary prose two elements above. An
              * icon owes 3.0, not 4.5, so this clears it with room. Pre-existing
              * rather than introduced by spec 119 — fixed here because the file is
              * on this stream's allowance and the fix is one token, and leaving a
              * 2.5:1 mark four lines from code this pass renormalised would read as
              * a defect this pass shipped.
              */}
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close job details"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>

          {/* "Open report" used to be a full-width filled button HERE. It is now
              the footer's primary for the ready state, which is where Himaad's
              Section 3 puts every state's actions — one filled primary per
              drawer, in one place, so the reader learns where to look once. */}
          {canView && (
            <div className="mt-3.5 flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[14px] leading-relaxed text-amber-800">
              <ClockIcon className="mt-px w-3.5 h-3.5 shrink-0" />
              <span>Opens the <b>saved snapshot</b> from {fmtTime(job.finishedAt ?? job.createdAt)}. Change a filter or re-run to refresh.</span>
            </div>
          )}
        </div>

        {/* SCROLLING MIDDLE */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/*
            * Section 3: the run summary leads on EVERY state, and the two special
            * panels are now purely diagnostic additions below it rather than
            * replacements for it.
            *
            * Previously a failed or expired run showed its panel INSTEAD of any
            * metadata, so the two states where a user most wants to know when it
            * ran, who ran it and what triggered it were the two states that did
            * not say. The failure timeline is genuinely worth keeping — for a run
            * that died the sequence is the story — so it stacks under the metric
            * rather than displacing it.
            */}
          <JobRunSummary job={shown} previousRun={history[0]} now={now} />

          {/*
            * Spec 167 — why there is no Download, where it can be read. `text-gray-600`
            * is #4a5565 in this build (Tailwind 4.2.1's oklch palette) and measures
            * 7.558:1 on the white panel; the disabled kebab item this replaces was
            * `text-gray-300`, #d1d5dc, 1.472:1, inside a menu nobody had opened.
            */}
          {downloadNote && (
            <p data-testid="job-download-note" className="mt-4 flex gap-2 text-[13px] leading-snug text-gray-600">
              <DownloadIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{downloadNote}</span>
            </p>
          )}

          {/*
            * `data-testid` is not decoration: spec 119's C2 normalised the type
            * scale of this file and `JobRunSummary` only, and these two panels
            * live in `JobFailedPanel.jsx` / `JobExpiredPanel.jsx`, which that
            * stream did not own. They still carry the old fractional scale (8
            * values and 2 respectively), INSIDE this drawer's own scroll column.
            * The C2 invariant test uses this hook to assert the drawer shell is
            * whole-pixel in every state while confining the known deviation to
            * these subtrees — so the gap is owned and pinned rather than
            * rediscovered as a new defect. It keeps passing once those files are
            * fixed. See spec 119 §2.6.
            */}
          {state === 'failed' && (
            <div data-testid="job-diagnostic-panel" className="mt-5 border-t border-gray-100 pt-4">
              <JobFailedPanel job={job} />
            </div>
          )}
          {state === 'expired' && (
            <div data-testid="job-diagnostic-panel" className="mt-5 border-t border-gray-100 pt-4">
              <JobExpiredPanel job={shown} gone={resultGone} />
            </div>
          )}

          {/* Filters used — from the run's own stored params, not re-derived. Read
              through `savedFilterChips`, which names each column from the report's
              definition (spec 167) and keeps a wire date range from being printed at
              the user; the stored params themselves are untouched (see that module).
              An Advanced (Exago) run never reaches it: its chips are the page's own words
              for what the run ran with (`exagoFiltersUsed`, spec 215). */}
          {/* Firm data (the report's saved filter names and values), so Pendo never captures
              it — as the Advanced Report page's own chips (spec 209 §3). */}
          {exagoFilters && (exagoFilters.chips.length > 0 || exagoFilters.notes.length > 0 || exagoFilters.saved) && (
            <div className={PENDO_IGNORE} data-testid="exago-filters-used">
              <div className="mb-2.5 mt-5 text-[12px] font-semibold uppercase tracking-[0.7px] text-gray-500">Filters used</div>
              {exagoFilters.chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="exago-run-filters">
                  {exagoFilters.chips.map((text, i) => (
                    <span key={`${i}:${text}`} className="inline-flex items-center rounded bg-cb-accent/10 px-2 py-0.5 text-[13px] text-cb-accent-text">
                      {text}
                    </span>
                  ))}
                </div>
              )}
              {exagoFilters.notes.map((note) => (
                <p key={note} className="mt-2 text-[13px] text-gray-600">{note}</p>
              ))}
              {exagoFilters.saved && (
                <div className="mt-3" data-testid="exago-saved-filters-used">
                  <p id={`job-${job.id}-saved-heading`} className="mb-1.5 text-[13px] font-semibold text-gray-600">{SAVED_IN_REPORT}</p>
                  {/* role="list": Tailwind's preflight drops list-style, and Safari then drops list semantics. */}
                  <ul role="list" aria-labelledby={`job-${job.id}-saved-heading`} className="flex flex-wrap gap-1.5">
                    {exagoFilters.saved.chips.map((text, i) => (
                      <li key={`${i}:${text}`} className="inline-flex items-center rounded bg-cb-accent/10 px-2 py-0.5 text-[13px] text-cb-accent-text">
                        {text}
                      </li>
                    ))}
                  </ul>
                  {exagoFilters.saved.notes.map((note) => (
                    <p key={note} className="mt-2 text-[13px] text-gray-600">{note}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          {!exagoJob && job.savedParams && Object.keys(job.savedParams).length > 0 && (
            <>
              <div className="mb-2.5 mt-5 text-[12px] font-semibold uppercase tracking-[0.7px] text-gray-500">Filters used</div>
              <div className="flex flex-wrap gap-1.5">
                {filterChips.map((chip) => (
                  <span key={chip.key} className="inline-flex items-center rounded bg-cb-accent/10 px-2 py-0.5 text-[13px] text-cb-accent-text">
                    {chip.label}: {chip.value}
                  </span>
                ))}
              </div>
            </>
          )}

          {history.length > 0 && (
            <>
              <div className="mb-2.5 mt-5 text-[12px] font-semibold uppercase tracking-[0.7px] text-gray-500">Previous runs of this report</div>
              <div className="overflow-hidden rounded-xl border border-gray-100">
                {history.map((h) => {
                  const hv = jobState(h.effectiveState || h.state)
                  return (
                    <div key={h.id} className="flex items-center justify-between border-b border-gray-100 px-3 py-2 text-[14px] last:border-b-0">
                      <span className="text-gray-700">{fmtWhen(h.createdAt, now)}</span>
                      <span className="flex items-center gap-2">
                        <span className="tabular-nums text-[13px] text-gray-500">{fmtRowCount(h.rowCount)}</span>
                        <span className={`rounded-full px-1.5 py-px text-[12px] font-semibold ${hv.badge}`}>{hv.label}</span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/*
          * FIXED FOOTER — Himaad's Section 3: ONE filled primary, one secondary,
          * one kebab, and what they are depends on the state.
          *
          *   ready         → Open report            | Run again, same filters
          *   didn't finish → Change filters & retry | Try again as-is
          *   running       → (no primary — see below) | Cancel run
          *   expired       → Run again, same filters | —
          *   scheduled     → (nothing to do yet)
          *
          * The running state has no primary on purpose. The design's is "Notify me
          * when it is ready", and there is no durable notification channel to wire
          * it to: what exists today is an in-memory per-tab browser notification
          * that needs the app open and the tab unfocused, and there is no
          * job-mutation route to arm anything that survives a closed laptop. A
          * button that appears to promise "we'll tell you" and silently means
          * "if you leave this tab open" is worse than no button, so the channel
          * decision comes first and the kebab says what happens today.
          */}
        <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
          {state === 'ready' && canView && (
            <Link
              to={`/reports/${job.reportId}?jobId=${job.id}`}
              className={PRIMARY_CTA}
            >
              Open report
              <ArrowRightIcon />
            </Link>
          )}

          {state === 'failed' && canRerun && (
            <button type="button" onClick={() => onRerun?.(job, 'edit')} className={PRIMARY_CTA}>
              Change filters &amp; retry
            </button>
          )}

          {/*
            * ONE label wherever this promise is made, because there is one
            * behaviour. CC-53051 shipped a "Run again, same filters" that ran the
            * user's PERSISTED filters instead (a 2019 range re-ran as 2020, 52
            * rows became 12), so the words "same filters" ARE the assertion this
            * button makes and it must read identically on the row kebab, here,
            * and anywhere else it appears.
            */}
          {canRerun && (state === 'ready' || state === 'expired') && (
            <button type="button" onClick={() => onRerun?.(job, 'rerun')} className={state === 'expired' ? PRIMARY_CTA : 'btn-cb-toolbar'}>
              Run again, same filters
            </button>
          )}
          {canRerun && state === 'failed' && (
            <button type="button" onClick={() => onRerun?.(job, 'rerun')} className="btn-cb-toolbar">
              Try again as-is
            </button>
          )}

          {/*
            * Cancel run — wired to the REAL server route, which has existed since
            * 2026-08-07 and which the UI never called. It marks the job
            * `cancelled` and deletes its stored result.
            *
            * The label is `Cancel run` and the confirmation copy stops at what it
            * can deliver: CB has no cancel hook, so the legacy query may run to
            * completion on the database. Saying "cancelled" flatly would be the
            * fail-soft this codebase keeps paying for. `Dismiss from queue` stays
            * in the kebab as the separate row-removal action — two different
            * things, two different words.
            *
            * `text-rc-danger-text!` carries a TRAILING BANG on purpose, and it is
            * the same unlayered trap the `PRIMARY_CTA` comment opens with, found on
            * the sibling 40 lines below it in R3 review. `.btn-cb-toolbar` sets
            * `color: var(--color-rc-ink-2)` at `src/index.css:478` and that file is
            * unlayered, so the plain `text-rc-danger-text` utility — emitted inside
            * `@layer utilities` — LOST, and this button rendered #374151, byte-
            * identical to `Try again as-is`. The only colour affordance separating
            * the destructive action from the benign one painted nothing. Tailwind
            * v4's important modifier compiles to
            * `color: var(--color-rc-danger-text) !important` (verified through the
            * installed 4.2.1 compiler), and an important author declaration beats an
            * unlayered normal one — so this needs no `src/index.css` edit and stays
            * inside spec 116's boundary. #b91c1c measures **6.302:1** on the
            * footer's `bg-gray-50` #f9fafb (6.468:1 on white), well clear of
            * SC 1.4.3's 4.5 — so making the utility win is safe as well as correct.
            */}
          {(state === 'queued' || state === 'running') && isOwner && onCancelRun && (
            <button
              type="button"
              onClick={() => onCancelRun(job)}
              className="btn-cb-toolbar text-rc-danger-text!"
            >
              Cancel run
            </button>
          )}

          {/*
            * When re-running is NOT possible, SAY SO — a missing button is
            * indistinguishable from a regression, which is exactly how CC-50233
            * came back from QA. This is the same in-place-explanation pattern the
            * download note already uses.
            */}
          {!canRerun && (state === 'ready' || state === 'failed' || state === 'expired') && (
            <span className="text-[13px] leading-tight text-gray-500">{rerunNote}</span>
          )}

          <span className="flex-1" />
          {/* Drop-UP: this kebab sits in the footer of a full-height fixed
              drawer, so a downward menu renders below the viewport. */}
          <KebabMenu items={footerItems} label="More job actions" direction="up" />
        </div>
      </aside>
    </>
  )
}

export default ReportJobDrawer
